/**
 * The announcement editor's form model (phase 7b): the draft the form edits,
 * made from an announcement (or blank for a new one), judged against the
 * contract's own schema, and turned into the create or update request.
 *
 * Times are edited as `datetime-local` text in the viewer's zone and sent as
 * ISO instants. A public announcement targets every tenant (the contract's
 * rule), so choosing "public" forces the audience to all.
 */
import { type Announcement, type AnnouncementKind, type AnnouncementSeverity, type CreateAnnouncementRequest, createAnnouncementRequestSchema, type StatusComponent, type UpdateAnnouncementRequest } from '@acp/api-contract/vendor.js';
import { fromLocalInput, toLocalInput } from './vendor.js';

export type AnnouncementDraft = {
  readonly kind: AnnouncementKind;
  readonly severity: AnnouncementSeverity;
  readonly title: string;
  readonly body: string;
  /** `datetime-local` text. */
  readonly startsAt: string;
  /** `datetime-local` text; empty: open-ended. */
  readonly endsAt: string;
  readonly public: boolean;
  readonly audience: 'all' | 'chosen';
  readonly tenantIds: readonly string[];
  readonly components: readonly StatusComponent[];
};

export function blankDraft(nowMs: number): AnnouncementDraft {
  return { kind: 'announcement', severity: 'info', title: '', body: '', startsAt: toLocalInput(new Date(nowMs).toISOString()), endsAt: '', public: false, audience: 'all', tenantIds: [], components: [] };
}

export function draftOf(a: Announcement): AnnouncementDraft {
  return {
    kind: a.kind,
    severity: a.severity,
    title: a.title,
    body: a.body,
    startsAt: toLocalInput(a.startsAt),
    endsAt: toLocalInput(a.endsAt),
    public: a.public,
    audience: a.tenantIds === null ? 'all' : 'chosen',
    tenantIds: a.tenantIds ?? [],
    components: a.components,
  };
}

/** Making it public forces every tenant; the chosen list is kept, to come back to. */
export function withPublic(draft: AnnouncementDraft, isPublic: boolean): AnnouncementDraft {
  return { ...draft, public: isPublic, audience: isPublic ? 'all' : draft.audience };
}

export type Judged = { readonly ok: true; readonly request: CreateAnnouncementRequest } | { readonly ok: false; readonly problems: readonly string[] };

/** The draft as a create request, checked with the contract's schema (its messages are the problems). */
export function judgeDraft(draft: AnnouncementDraft): Judged {
  const problems: string[] = [];
  const startsAt = fromLocalInput(draft.startsAt);
  if (startsAt === null) problems.push('Start: a date and time is required.');
  const endsAt = fromLocalInput(draft.endsAt);
  if (draft.endsAt.trim() !== '' && endsAt === null) problems.push('End: not a date and time.');
  if (draft.audience === 'chosen' && draft.tenantIds.length === 0) problems.push('Audience: choose at least one tenant, or target every tenant.');
  const candidate = {
    kind: draft.kind,
    severity: draft.severity,
    title: draft.title,
    body: draft.body,
    startsAt: startsAt ?? '',
    endsAt,
    public: draft.public,
    tenantIds: draft.audience === 'all' ? null : [...draft.tenantIds],
    components: [...draft.components],
  };
  if (problems.length > 0) return { ok: false, problems };
  const parsed = createAnnouncementRequestSchema.safeParse(candidate);
  if (!parsed.success) return { ok: false, problems: parsed.error.issues.map((i) => `${i.path.length === 0 ? 'Announcement' : i.path.join('.')}: ${i.message}`) };
  return { ok: true, request: parsed.data };
}

/** `edited` unless it is `stored` shown to the minute (the form's precision): then `stored`, seconds and all. */
function samePrecise(edited: string | null, stored: string | null): string | null {
  return edited !== null && stored !== null && toLocalInput(edited) === toLocalInput(stored) ? stored : edited;
}

/**
 * An update naming every field (the draft is the whole announcement) and the version it was made from. A time the
 * form showed and nobody changed keeps its stored seconds: the form is minute-precise, and truncating an untouched
 * start could put it after an end in the same minute.
 */
export function editOf(request: CreateAnnouncementRequest, base: Announcement): UpdateAnnouncementRequest {
  return { ...request, startsAt: samePrecise(request.startsAt, base.startsAt) ?? request.startsAt, endsAt: samePrecise(request.endsAt, base.endsAt), version: base.version };
}

/** "End now" in the minute-precise form: the next whole minute, so the end is after a start made earlier this minute. */
export function endNowInput(nowMs: number): string {
  const MINUTE = 60_000;
  return toLocalInput(new Date(Math.ceil((nowMs + 1) / MINUTE) * MINUTE).toISOString());
}

/** Whether the draft differs from the announcement it was made from. */
export function isDraftDirty(draft: AnnouncementDraft, base: Announcement): boolean {
  const from = draftOf(base);
  const same =
    draft.kind === from.kind &&
    draft.severity === from.severity &&
    draft.title === from.title &&
    draft.body === from.body &&
    draft.startsAt === from.startsAt &&
    draft.endsAt === from.endsAt &&
    draft.public === from.public &&
    draft.audience === from.audience &&
    (draft.audience === 'all' || sameSet(draft.tenantIds, from.tenantIds)) &&
    sameSet(draft.components, from.components);
  return !same;
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((x) => b.includes(x));
}
